<template lang="html">
  <div class="graphs">
    <div v-if="linechart" class="ui segment">
      <h3 class="header">Performance over Time</h3>
      <line-chart
        :key="'line-' + themeVersion"
        :stacked="true"
        :library="lineChartOptions"
        :data="lineChartDataUrl"
      />
      <div class="ui top right attached label">
        <div class="ui buttons tiny compact">
          <div class="ui button" @click="setLineChartRange([6, 'months'])">
            6 months
          </div>
          <div class="ui button" @click="setLineChartRange([3, 'months'])">
            3 months
          </div>
          <div class="ui button" @click="setLineChartRange([1, 'week'])">
            1 week
          </div>
        </div>
      </div>
    </div>
    <div class="ui three column stackable grid">

      <div
        v-for="t in visibleMetricTypes"
        v-if="sumStatisticType(t) > 0 || disabledDatasets[t]"
        class="column"
      >
        <div
          class="ui hovering fluid raised card report-rollup-card pointer padding-top-small"
        >
          <div
            v-if="sumStatisticType(t) > 0 || disabledDatasets[t]"
            class="wrap-graph centered fpo"
            :class="t"
          >
            <div class="pieChartCenter">
              <div class="value">
                <div class="fit">
                  <span v-if="t == 'ad_spend' || t == 'revenue'">$</span>{{ sumStatisticType(t) | abbreviateNumber(10000000) }}
                </div>
              </div>
              <div class="label">
                <div class="fit">
                  {{ metricLabel(t) | capitalize }}
                </div>
              </div>
            </div>
            <pie-chart
              :key="'pie-' + t + '-' + themeVersion"
              :ref="'chart-' + t"
              class="piechart"
              height="400px"
              :legend="false"
              :library="pieChartOptions"
              :donut="true"
              :data="chartData(t)"
              :colors="chartColors(t)"
            />
          </div>
          <div
            v-if="chartData(t)"
            class="ui horizontal list center aligned pie-chart-data"
          >
            <div
              v-for="(value, label, i) in chartData(t)"
              class="item"
              :class="{ labelDisabled: disabledDatasets[`${t}-${label}`] }"
              @click.stop.prevent="legendClick(t, label)"
            >
              <span
                class="ui empty circular label"
                :sstyle="'background-color: ' + colors[label]"
                :style="'background-color: ' + chartColors(t)[i]"
              />
              {{ label }}
            </div>
          </div>
          <div
            class="ui center aligned text hint margin-bottom-medium margin-top-small"
          >
            <strong class="capitalize">{{ metricLabel(t) }}:</strong>
            <span v-html="metricTypeHint(t)" />
          </div>
        </div>
      </div>
    </div>

    <div class="ui equal width grid" />

    <div
      v-if="youTubeDeliverables.length > 0"
      class="youtubeChart ui centered grid padding-x-medium pointer padding-y-medium"
    >
      <!-- <bar-chart
        height="150px"
        :data="youTubeData"
        :colors="[colors['Youtube'], '#ccc']"
        legend="bottom"
      ></bar-chart> -->

      <div class="ui statistics centered pieChartCenter">
        <div
          v-for="obj in youTubeData"
          class="ui statistic card padding-xy-small"
        >
          <div class="value">
            {{ obj.data[0][1] | abbreviateNumber(10000000) }}
          </div>
          <div class="label">
            <span style="text-transform: none !important">YouTube </span>
            {{ obj.name }}
          </div>
        </div>
      </div>

      <br />
    </div>
    <div
      class="ui centered text hint margin-bottom-medium margin-top-small"
      v-if="youTubeDeliverables.length > 0"
    >
      These metrics are not aggregated alongside other content channels (<a
        href="http://help.influencekit.com/en/articles/2138024-what-metrics-are-available-on-reports-and-what-do-they-mean"
        target="_blank"
        >learn why</a
      >)
    </div>
  </div>
</template>

<script>
import Vue from "vue/dist/vue.esm";
import Chartkick from "chartkick";
import VueChartkick from "vue-chartkick";
import fitMultiple from "functions/fitMultiple";
import fitty from "fitty";
import _ from "lodash";
import { getChartThemeColors, observeThemeChange } from "utils/chartTheme";
import {
  aggregatableDeliverables,
  isAggregatable,
} from "lib/summaryMetricCatalog.mjs";
import { chartTypes, defaultChartMetrics } from "lib/reportChartCatalog.mjs";

const { moment } = window;

Vue.use(VueChartkick, { Chartkick });

export default {
  components: {},
  props: {
    deliverables: Array,
    linechart: Boolean,
    colors: {
      type: Object,
    },
    hideImpressionsChart: {
      type: Boolean,
      default: false,
    },
    hideViewsChart: {
      type: Boolean,
      default: false,
    },
    hideEngagementsChart: {
      type: Boolean,
      default: false,
    },
    // A saved report's own chart list (Report#chart_metrics, or the owner's
    // live pick). When it is set the hide-*-chart flags are already folded in
    // server-side; null means a surface with no report of its own.
    chartMetrics: {
      type: Array,
      default: null,
    },
  },
  data() {
    return {
      themeVersion: 0,
      lineChartStartDate: moment().subtract(6, "months").format(),
      lineChartEndDate: moment().format(),
      disabledDatasets: [],
    };
  },
  computed: {
    pieChartOptions() {
      const _v = this.themeVersion;
      const t = getChartThemeColors();
      return {
        border: false,
        cutoutPercentage: 80,
        legend: {
          position: "bottom",
          labels: {
            usePointStyle: true,
            fontSize: 16,
            fontColor: t.fontColor,
            fontFamily: "Work Sans",
            fontStyle: "500",
          },
        },
      };
    },
    lineChartOptions() {
      const _v = this.themeVersion;
      const t = getChartThemeColors();
      return {
        spanGaps: true,
        elements: {
          line: {
            tension: 0.05,
            fill: false,
          },
        },
        legend: {
          labels: {
            fontColor: t.legendColor,
          },
        },
        tooltips: {
          backgroundColor: t.tooltipBackgroundColor,
          titleFontColor: t.tooltipTitleColor,
          bodyFontColor: t.tooltipBodyColor,
          borderColor: t.tooltipBorderColor,
          borderWidth: 1,
          callbacks: {
            label(tooltipItem) {
              return parseInt(tooltipItem.yLabel, 10).toLocaleString();
            },
          },
        },
        scales: {
          yAxes: [
            {
              id: "y-axis-engagements",
              position: "left",
              type: "linear",
              ticks: {
                min: 0,
                fontColor: t.tickColor,
                callback(value) {
                  return value.toLocaleString();
                },
              },
              gridLines: {
                color: t.gridColor,
              },
            },
            {
              id: "y-axis-impressions",
              position: "right",
              type: "linear",
              ticks: {
                min: 0,
                fontColor: t.tickColor,
                callback(value) {
                  return value.toLocaleString();
                },
              },
              gridLines: {
                color: t.gridColor,
              },
            },
          ],
        },
      };
    },
    visibleMetricTypes() {
      if (this.chartMetrics) return chartTypes(this.chartMetrics);

      return chartTypes(
        defaultChartMetrics({
          hideImpressions: this.hideImpressionsChart,
          hideViews: this.hideViewsChart,
          hideEngagements: this.hideEngagementsChart,
        })
      );
    },
    // Shared with the Summary section's totals — one definition of what belongs in
    // an aggregate, so a card and the donut beneath it can never print different
    // numbers for the same metric again.
    deliverablesExcludingYouTube() {
      return aggregatableDeliverables(this.deliverables);
    },
    youTubeDeliverables() {
      return this.deliverables.filter((d) => !isAggregatable(d));
    },
    youTubeData() {
      const $this = this;
      let data = [];
      ["impressions", "views", "engagements"].forEach(function (metric_type) {
        let sum = 0;

        $this.youTubeDeliverables.forEach((d) => {
          d.statisticsValues.forEach((s) => {
            if (s.type !== metric_type) {
              return;
            }
            sum += s.total.value;
          });
        });
        if (sum > 0) {
          let metric_type_label =
            metric_type == "engagements" ? "Likes & Comments" : metric_type;
          data.push({
            name: metric_type_label.toUpperCase(),
            data: [["YouTube", sum]],
          });
        }
      });
      return data;
    },
    deliverableIds() {
      if (this.deliverables.length > 0) {
        return _.map(this.deliverables, "id");
      }
      return [];
    },
    lineChartDataUrl() {
      return `/statistics?deliverable_ids=${this.deliverableIds.join(
        ","
      )}&startDate=${this.lineChartStartDate || ""}&endDate=${
        this.lineChartEndDate || ""
      }`;
    },
  },
  mounted() {
    this.themeStop = observeThemeChange(() => {
      this.themeVersion++;
    });
    this.$nextTick(() => {
      this.fitChartValues();
      this.resizeCharts();
    });
  },
  beforeDestroy() {
    if (this.themeStop) this.themeStop();
  },
  updated() {
    this.$nextTick(() => {
      this.fitChartValues();
    });
  },
  watch: {
    // Charts get created while their parent column is mid-transition from
    // v-if=false → true (deliverables loaded), so Chart.js v2 measures
    // the parent as 0×0 and locks the canvas there forever. Re-measuring
    // after the DOM has settled forces the canvas back to real dimensions.
    deliverables() {
      this.$nextTick(() => this.resizeCharts());
    },
  },
  methods: {
    metricLabel(type) {
      const labels = {
        impressions_unique: 'reach',
        outbound_clicks: 'link clicks',
        paid_impressions: 'Paid Impressions',
        ad_spend: 'Ad Spend',
        paid_reach: 'Paid Reach'
      };
      return labels[type] || type;
    },
    legendClick(type, label) {
      const chart = this.chartObject(type);
      const index = _.indexOf(chart.data.labels, label);

      var i, ilen, meta;

      for (i = 0, ilen = (chart.data.datasets || []).length; i < ilen; ++i) {
        meta = chart.getDatasetMeta(i);
        // console.log(meta);
        // toggle visibility of index if exists

        if (meta.data[index]) {
          // Check if this is the last visible dataset
          const visibleCount = meta.data.filter(d => !d.hidden).length;

          // Prevent disabling if this is the last visible dataset
          if (visibleCount === 1 && !meta.data[index].hidden) {
            return;
          }

          meta.data[index].hidden = !meta.data[index].hidden;

          if (meta.data[index].hidden) {
            this.$set(this.disabledDatasets, `${type}-${label}`, true);
          } else {
            this.$delete(this.disabledDatasets, `${type}-${label}`);
          }
        }
      }

      chart.update();
    },
    chartObject(t) {
      if (this.$refs["chart-" + t] && this.$refs["chart-" + t][0]) {
        return this.$refs["chart-" + t][0].chart.chart;
      } else {
        return null;
      }
    },
    resizeCharts() {
      this.visibleMetricTypes.forEach((t) => {
        const c = this.chartObject(t);
        if (c && typeof c.resize === "function") c.resize();
      });
    },
    metricTypeHint(type) {
      switch (type) {
        case "impressions":
          return "The number of times your content was visible to users";
        case "views":
          return "The number of times users read or watched your content";
        case "engagements":
          return "The number of times users interacted with your content";
        // Summed per post, so someone who saw three posts counts three times.
        // Saying "unique" here would overstate it as audience size.
        case "impressions_unique":
          return "Each post's reach, added together. Someone reached by several posts is counted once per post";
        case "outbound_clicks":
          return "The number of times users clicked a link in your content";
        case "revenue":
          return "The amount of revenue attributed to this campaign";
      }
    },
    sumStatisticType(t) {
      let i = 0;
      const data = this.chartData(t);

      Object.keys(data).forEach((k) => {
        if (!this.disabledDatasets[`${t}-${k}`]) {
          i += data[k];
        }
      });
      return i;
    },
    chartColors(type) {
      const r = [];
      Object.keys(this.chartData(type)).forEach((k) => {
        if (k.includes("Other")) {
          r.push(this.colors["Other"]);
        } else {
          r.push(this.colors[k]);
        }
      });
      return r;
    },
    chartData(type) {
      const data = {};
      this.deliverablesExcludingYouTube.forEach((d) => {
        if (!d.statisticsValues) {
          return;
        }
        let sum = 0;

        d.statisticsValues.forEach((s) => {
          if (s.type !== type) {
            return;
          }
          sum += s.total.value;
        });

        if (d.inferred_type == "other") {
          d.inferred_type = `Other (${d.name})`;
        }

        if (data[d.inferred_type]) {
          data[d.inferred_type] += sum;
        } else {
          data[d.inferred_type] = sum;
        }
      });
      const filteredData = {};
      Object.keys(data).forEach((dataType) => {
        if (data[dataType] > 0) {
          filteredData[this.typeAsLabel(dataType)] = data[dataType];
        }
      });
      return filteredData;
    },
    typeAsLabel(type) {
      switch (type) {
        case "url":
          return "Articles";
        case "instagram_stories":
          return "Instagram Stories";
        case "one_signal":
          return "Push Notification (OneSignal)";
        case "push_engage":
          return "Push Notification (PushEngage)";
        case "google":
          return "Articles";
        case "omny_studio":
          return "Podcast (Omny Studio)";
        case "tik_tok":
          return "TikTok";
        case "youtube":
          return "YouTube";
        case "constantcontact":
          return "Constant Contact";
        case "campaignmonitor":
          return "Campaign Monitor";
        case "short_io":
          return "Short.io";
        case "paid_impressions":
          return "Paid Impressions";
        case "ad_spend":
          return "Ad Spend";
        case "paid_reach":
          return "Paid Reach";
        case "paid_clicks":
          return "Paid Clicks";
        default:
          return type[0].toUpperCase() + type.slice(1);
      }
    },
    fitChartValues() {
      fitMultiple(this.$el.querySelectorAll(".graphs .column .value .fit"), {
        minSize: 40,
        maxSize: 100,
      });

      fitty(".graphs .pieChartCenter .label .fit", {
        maxSize: 30,
        minSize: 20,
      });
    },
    setLineChartRange(args) {
      this.lineChartStartDate = moment()
        .subtract(...args)
        .format();
      this.lineChartEndDate = moment().format();
    },
  },
};
</script>

<style lang="scss" scoped>
// Three DESIGN.md violations lived in this block, on the headline metric of the
// app's highest-traffic surface.
//
// 1. `font-family: lato, helvetica neue, Arial, ...`. Lato is not loaded
//    anywhere in this app, so these fell through to Helvetica/Arial — measured
//    in the live DOM, the report's big numbers rendered in Arial while
//    document.body is DM Sans. DESIGN.md §3's One Family Rule: "DM Sans does
//    every job. Reaching for a second typeface [...] is prohibited." Removing
//    the declaration inherits DM Sans rather than naming it again.
// 2. `text-transform: uppercase` — §3's No-Tracked-Eyebrow Rule. The label is
//    authored lowercase ("impressions"), so dropping the transform renders the
//    authored text; it does not need re-casing.
// 3. `color: #1b1c1d` — a hardcoded near-black, invisible-on-invisible in dark
//    mode. --text-primary is the same colour in light mode and correct in dark.
//
// Only two `lato` references existed in the whole tree and both were here, so
// this is a leftover from an older theme rather than a system to migrate.
.graphs .pieChartCenter,
.pieChartCenter .ui.statistics .statistic {
  color: var(--text-primary);
  text-align: center;

  .value,
  > .value {
    max-width: 100% !important;
    font-weight: 600;
    margin-bottom: 20px;
  }
  .label,
  > .label {
  }
}

.ui.horizontal.list .item {
  user-select: none;
}
.item.labelDisabled {
  opacity: 0.65;
  text-decoration: line-through;
}

.piechart {
  z-index: 3;
}

/* The rings are declared `height="400px"` on the component. Three of those
   across a 1280 viewport is ~700px of chart for three numbers — the entire
   first screen of a report, before a single deliverable is visible, with the
   page's actual content pushed below the fold.

   DESIGN.md is explicit that a report should "lead with a story — hierarchy
   first, secondary metrics recede" and lists the "cluttered analytics dump" as
   an anti-reference. The ring is not the story; the number in it is.

   260px keeps the ring legible and its platform split readable while giving
   the page back roughly 140px per row. The phone rule below is unchanged: one
   ring per row at 250px is proportionate there. */
.graphs .piechart {
  height: 260px !important;
}

@media (max-width: 768px) {
  .graphs .piechart {
    height: 250px !important;
  }
  .graphs .pie-chart-data {
    margin-top: 16px;
  }
}
</style>
